import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import Toggle from "@/components/ui/Toggle";
import { useToast } from "@/components/ui/Toast";
import { adminService } from "@/services/apiServices";

const LENGTH_OPTIONS = [
  { value: "8", label: "8 characters" },
  { value: "10", label: "10 characters" },
  { value: "12", label: "12 characters" },
];

const EXPIRY_OPTIONS = [
  { value: "Never", label: "Never" },
  { value: "30 days", label: "30 days" },
  { value: "60 days", label: "60 days" },
  { value: "90 days", label: "90 days" },
];

const TIMEOUT_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "60 minutes" },
  { value: "120", label: "120 minutes" },
];

export default function SecurityTab() {
  const toast = useToast();
  const [policy, setPolicy] = useState({
    minLength: "8",
    uppercase: true,
    number: true,
    special: false,
    expiry: "Never",
  });
  const [session, setSession] = useState({
    timeout: "30",
    forceLogout: true,
  });
  const [access, setAccess] = useState({
    multiDevice: true,
    ipWhitelist: "203.0.113.0/24\n198.51.100.10",
  });

  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);

  useEffect(() => {
    adminService
      .getSettings()
      .then((res) => {
        if (res && res.data && res.data.security) {
          const s = res.data.security;
          if (s.policy) setPolicy((p) => ({ ...p, ...s.policy }));
          if (s.session) setSession((sess) => ({ ...sess, ...s.session }));
          if (s.access) setAccess((a) => ({ ...a, ...s.access }));
        }
      })
      .catch(() => undefined);
  }, []);

  function savePolicy() {
    setSavingPolicy(true);
    adminService
      .updateSecuritySettings({ policy })
      .then((res: any) => {
        setSavingPolicy(false);
        if (res?.data?.security?.policy) {
          setPolicy(res.data.security.policy);
        }
        toast.success("Password policy saved to database");
      })
      .catch((err: any) => {
        setSavingPolicy(false);
        toast.error(err?.message || "Failed to save password policy");
      });
  }

  function saveSession() {
    setSavingSession(true);
    adminService
      .updateSecuritySettings({ session })
      .then((res: any) => {
        setSavingSession(false);
        if (res?.data?.security?.session) {
          setSession(res.data.security.session);
        }
        toast.success("Session settings saved to database");
      })
      .catch((err: any) => {
        setSavingSession(false);
        toast.error(err?.message || "Failed to save session settings");
      });
  }

  function saveAccess() {
    setSavingAccess(true);
    adminService
      .updateSecuritySettings({ access })
      .then((res: any) => {
        setSavingAccess(false);
        if (res?.data?.security?.access) {
          setAccess(res.data.security.access);
        }
        toast.success("Access control saved to database");
      })
      .catch((err: any) => {
        setSavingAccess(false);
        toast.error(err?.message || "Failed to save access control");
      });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Password Policy */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Password Policy</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Rules applied to all account passwords</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Minimum Length"
            options={LENGTH_OPTIONS}
            value={policy.minLength}
            onChange={(e) => setPolicy({ ...policy, minLength: e.target.value })}
          />
          <Select
            label="Password Expiry"
            options={EXPIRY_OPTIONS}
            value={policy.expiry}
            onChange={(e) => setPolicy({ ...policy, expiry: e.target.value })}
          />
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-line pt-5">
          <Checkbox
            label="Require an uppercase letter"
            checked={policy.uppercase}
            onChange={(e) => setPolicy({ ...policy, uppercase: e.target.checked })}
          />
          <Checkbox
            label="Require a number"
            checked={policy.number}
            onChange={(e) => setPolicy({ ...policy, number: e.target.checked })}
          />
          <Checkbox
            label="Require a special character"
            checked={policy.special}
            onChange={(e) => setPolicy({ ...policy, special: e.target.checked })}
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={savePolicy} loading={savingPolicy} leftIcon={<Save className="h-4 w-4" />}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Session */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Session</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Session duration and security behaviour</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Session Timeout"
            options={TIMEOUT_OPTIONS}
            value={session.timeout}
            onChange={(e) => setSession({ ...session, timeout: e.target.value })}
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-4 border-t border-line pt-5">
          <div>
            <p className="text-sm font-medium text-ink">Force logout on password change</p>
            <p className="mt-0.5 text-xs text-ink-muted">Sign out all other devices when the password is changed</p>
          </div>
          <Toggle
            checked={session.forceLogout}
            onChange={(v) => setSession({ ...session, forceLogout: v })}
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={saveSession} loading={savingSession} leftIcon={<Save className="h-4 w-4" />}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Access Control */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Access Control</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Restrict and control retailer access</p>

        <div className="mt-5 flex items-center justify-between gap-4 border-b border-line pb-5">
          <div>
            <p className="text-sm font-medium text-ink">Allow retailer login from multiple devices</p>
            <p className="mt-0.5 text-xs text-ink-muted">Retailers can be signed in on more than one device at once</p>
          </div>
          <Toggle checked={access.multiDevice} onChange={(v) => setAccess({ ...access, multiDevice: v })} />
        </div>

        <div className="mt-5">
          <Textarea
            label="IP Whitelist"
            rows={4}
            value={access.ipWhitelist}
            onChange={(e) => setAccess({ ...access, ipWhitelist: e.target.value })}
            helper="One IP address per line, leave blank to allow all"
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={saveAccess} loading={savingAccess} leftIcon={<Save className="h-4 w-4" />}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}